class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay && !overlay.dataset.bound) {
      overlay.dataset.bound = 'true';
      overlay.addEventListener('click', this.close.bind(this));
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.cartDrawerBound === 'true') return;

    cartLink.dataset.cartDrawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.bindOverlay();
    this.classList.add('animate');

    requestAnimationFrame(() => {
      this.classList.add('active');
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  /**
   * renderContents — called with the parsed response from /cart/add.js
   * which includes a `sections` object containing fresh HTML for
   * cart-drawer and cart-icon-bubble.
   *
   * This is the SINGLE authoritative method for updating the drawer.
   * product-form.js, cart-drawer-items, and homepassion-cart-ajax-fix
   * all funnel through here (or the equivalent logic).
   */
  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) return;

    const drawerHTML = parsedState.sections['cart-drawer'];
    const bubbleHTML = parsedState.sections['cart-icon-bubble'];
    const parser = new DOMParser();

    // --- Update cart-drawer HTML ---
    if (drawerHTML) {
      const newDoc = parser.parseFromString(drawerHTML, 'text/html');
      const newDrawer = newDoc.querySelector('cart-drawer');
      const newDrawerInner = newDoc.querySelector('#CartDrawer');
      const currentDrawerInner = this.querySelector('#CartDrawer');

      if (newDrawer) {
        // Replace inner content — keeps the custom element instance alive
        this.innerHTML = newDrawer.innerHTML;
      } else if (newDrawerInner && currentDrawerInner) {
        currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
      }

      // Remove all empty-state classes
      this.classList.remove('is-empty');
      this.querySelector('.drawer__inner')?.classList.remove('is-empty');
      this.querySelector('cart-drawer-items')?.classList.remove('is-empty');

      // Hide the empty-state block if still in DOM
      const emptyBlock = this.querySelector('.drawer__inner-empty');
      if (emptyBlock) emptyBlock.style.display = 'none';

      // Re-bind overlay (innerHTML replaced so old listener is gone)
      this.bindOverlay();
    }

    // --- Update cart icon bubble ---
    if (bubbleHTML) {
      const newDoc = parser.parseFromString(bubbleHTML, 'text/html');
      const newBubble = newDoc.querySelector('#cart-icon-bubble');
      const currentBubble = document.querySelector('#cart-icon-bubble');
      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }
    }

    // --- Open drawer + highlight just-added item ---
    setTimeout(() => {
      this.open();
      const firstItem = this.querySelector('.premium-cart-item, .cart-item');
      if (firstItem) {
        firstItem.classList.add('premium-cart-item--just-added');
        setTimeout(() => firstItem.classList.remove('premium-cart-item--just-added'), 650);
      }
    }, 30);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#cart-icon-bubble',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

// Expose a global helper so homepassion-cart-ajax-fix.js can call renderContents
// via a consistent API without duplicating DOM update logic.
window.HomePassionCartDrawer = {
  render: function (parsedState, trigger) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.renderContents(parsedState);
  },
  refresh: async function () {
    const url =
      (window.routes?.cart_url || '/cart') +
      '?sections=cart-drawer,cart-icon-bubble&sections_url=' +
      encodeURIComponent(window.location.pathname);
    const resp = await fetch(url, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    const sections = await resp.json();
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.renderContents({ sections });
    return sections;
  },
  open: function (trigger) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.classList.remove('is-empty');
    if (typeof drawer.open === 'function') drawer.open(trigger);
    else {
      drawer.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
    }
  },
};